/* global __DEV__ */
/* eslint-disable global-require */

import queryString from 'query-string';
import { fetchJournalRubrics, loggedInRedirect } from 'shared/helpers/routes';
import UnauthorizedError from 'shared/classes/UnauthorizedError';
import { AVAILABLE_LOCALES } from 'shared/constants/locale';
import { setPreviousPath, setCurrentPath, setCurrentRouteName } from 'shared/actions/history';
import { userLogout } from 'shared/actions/user';
import staticPagesNavigation from 'shared/constants/staticPagesNavigation';
import URL from 'shared/constants/url';

// Hacking due to a webpack bug.
// https://github.com/gaearon/react-hot-loader/issues/324
if (__DEV__) {
  // require('./add-parts');
  // require('./add');
  // require('./edit');
  // require('./login');
  // require('./advert');
  // require('./search');
  // require('./dealer');
  // require('./dealers');
  // require('./profile/my');
  // require('./profile/favorites');
  // require('./profile/billing');
  // require('./not-found');
  // require('./home');
  // require('./firms');
  // require('./firms/firm');
  // require('./invoice');
  // require('./auto-parts');
  // require('./journal/news-page');
  // require('./journal/news');
}

const searchPages = [
  'avto',
  'mototsikl',
  'skuter',
  'kvadrotsikl',
  'karting',
  'moped',
  'baggi',
  'tritsikl',
  'gruzovye',
  'avtobus',
  'pritsepy',
  'spetstehnika',
  'vodnyi-transport',
].map(item => ({
  path: `/${item}`,
  name: 'search',
  children: [
    {
      path: '',
      name: item,
      load: () => import(/* webpackChunkName: 'search' */ './search'),
    },
    {
      path: '*',
      load: () => import(/* webpackChunkName: 'search' */ './search'),
    },
  ],
}));

const avtosalonyPages = ['mototsikl', 'pritsepy', 'avtobus', 'spetstehnika', 'gruzovye'].map(
  item => ({
    path: `/${item}`,
    name: `dealers-${item}`,
    children: [
      {
        path: '',
        name: item,
        load: () => import(/* webpackChunkName: 'dealers' */ './dealers'),
      },
      {
        path: '*',
        load: () => import(/* webpackChunkName: 'dealers' */ './dealers'),
      },
    ],
  }),
);

const sparesPages = ['mototsikl', 'pritsepy', 'avtobus', 'spetstehnika', 'gruzovye'].map(item => ({
  path: `/${item}`,
  name: `spares-${item}`,
  children: [
    {
      path: '',
      name: item,
      load: () => import(/* webpackChunkName: 'auto-parts' */ './spareparts'),
    },
    {
      path: '*',
      load: () => import(/* webpackChunkName: 'auto-parts' */ './spareparts'),
    },
  ],
}));

const pages = staticPagesNavigation.map(({ slug }) => ({
  path: `/${slug.replace(/\//g, '')}`,
  load: () => import(/* webpackChunkName: 'pages' */ `./pages/${slug.replace(/\//g, '')}`),
}));

// The top-level (parent) route
const routes = {
  path: [...AVAILABLE_LOCALES.slice(1).map(locale => `/${locale}`), '/'],

  // Keep in mind, routes are evaluated in order
  children: [
    {
      path: '/:locationSlug?',
      load: () => import(/* webpackChunkName: 'home' */ './home'),
    },

    ...pages,

    {
      path: '/add/zapchasti',
      load: () => import(/* webpackChunkName: 'add-parts' */ './add-parts'),
    },
    {
      path: '/add/:transportSlug?',
      load: () => import(/* webpackChunkName: 'add' */ './add'),
    },

    {
      path: '/addv2/:transportSlug?',
      load: () => import(/* webpackChunkName: 'add' */ './addV2'),
    },

    {
      path: '/moto/:locationSlug?',
      load: () => import(/* webpackChunkName: 'moto' */ './moto'),
    },
    {
      path: '/commercial/:locationSlug?',
      load: () => import(/* webpackChunkName: 'moto' */ './commercial'),
    },
    ...searchPages,
    {
      path: '/avtosalony',
      children: [
        {
          path: '',
          name: 'dealers',
          load: () => import(/* webpackChunkName: 'dealers' */ './dealers'),
        },
        ...avtosalonyPages,
        {
          path: '*',
          name: 'dealers',
          load: () => import(/* webpackChunkName: 'dealers' */ './dealers'),
        },
        {
          path: '/:slug',
          // name: 'dealer',
          // load: () => import(/* webpackChunkName: 'dealer' */ './dealer'),
          children: [
            {
              path: '',
              name: 'dealer',
              load: () => import(/* webpackChunkName: 'dealer' */ './dealer'),
            },
            {
              path: '/blog',
              name: 'dealer-blog',
              load: () => import(/* webpackChunkName: 'dealer' */ './dealer/blog'),
            },
            {
              path: '/about',
              name: 'dealer-about',
              load: () => import(/* webpackChunkName: 'dealer' */ './dealer/about'),
            },
          ],
        },
      ],
    },
    {
      path: '/edit/:id',
      name: 'edit',
      load: () => import(/* webpackChunkName: add */ './edit'),
    },
    {
      path: '/edit-parts/:id',
      name: 'edit',
      load: () => import(/* webpackChunkName: edit-parts */ './edit-parts'),
    },
    {
      path: URL.registration(),
      action: loggedInRedirect,
      children: [
        {
          path: '/',
          load: () => import(/* webpackChunkName: 'registration' */ './loginUs'),
        },
      ],
    },
    {
      path: URL.login(),
      action: loggedInRedirect,
      children: [
        {
          path: '/',
          load: () => import(/* webpackChunkName: 'registration' */ './loginUs'),
        },
      ],
    },
    {
      path: URL.recovery(),
      action: loggedInRedirect,
      children: [
        {
          path: '/',
          load: () => import(/* webpackChunkName: 'registration' */ './loginUs'),
        },
      ],
    },
    {
      path: '/profile',
      load: () => import(/* webpackChunkName: 'profile' */ './profile'),
    },
    {
      path: URL.verify(),
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'verify' */ './loginUs'),
        },
      ],
    },
    {
      path: '/katalog-kompaniy',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'firms' */ './firms'),
        },
        {
          path: '*',
          load: () => import(/* webpackChunkName: 'firms' */ './firms'),
        },
        {
          path: '/:id',
          children: [
            {
              path: '/:tab?',
              load: () => import(/* webpackChunkName: 'firm' */ './firms/firm'),
            },
          ],
        },
      ],
    },
    {
      path: '/dialogs',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'dialogs' */ './profile/dialogs'),
        },
        {
          path: '/:dialogId',
          load: () => import(/* webpackChunkName: 'Dialogs' */ './profile/dialogs'),
        },
      ],
    },

    {
      path: '/service',
      children: [
        {
          path: '/ocenka-avto',
          load: () => import(/* webpackChunkName: 'ocenka-avto' */ './service/evaluate-car'),
        },
        {
          path: '/prygon-avto',
          load: () => import(/* webpackChunkName: 'prygon-avto' */ './service/car-drive'),
        },
        {
          path: '/search-subscriptions',
          load: () =>
            import(/* webpackChunkName: 'search-subscriptions' */ './service/search-subscriptions'),
        },
        {
          path: '/:id',
          children: [
            {
              path: '/:tab?',
              load: () => import(/* webpackChunkName: 'firm' */ './service/car-drive/firm'),
            },
          ],
        },
        {
          path: '/',
          load: () => import(/* webpackChunkName: 'ocenka-avto' */ './service/evaluate-car'),
        },
      ],
    },
    {
      path: '/my',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'my' */ './profile/my'),
        },
        {
          path: '/:type',
          load: () => import(/* webpackChunkName: 'my' */ './profile/my'),
        },
      ],
    },

    {
      path: '/zapchasti',
      children: [
        ...sparesPages,
        {
          path: '',
          load: () => import(/* webpackChunkName: 'auto-parts' */ './spareparts'),
        },
        {
          path: '*',
          load: () => import(/* webpackChunkName: 'auto-parts' */ './spareparts'),
        },
        {
          path: '/:id',
          load: () =>
            import(/* webpackChunkName: 'advert-auto-part' */ './spareparts/spareparts-singlepage'),
        },
      ],
    },

    {
      path: '/billing',
      load: () => import(/* webpackChunkName: 'billing' */ './profile/billing'),
    },

    {
      path: '/insurance',
      children: [
        {
          path: '',
          load: () => import(/* webpackChunkName: 'insurance' */ './profile/insurance'),
        },
        {
          path: '/by-car-number',
          load: () =>
            import(/* webpackChunkName: 'by-car-number' */ './profile/insurance/ByCarNumber'),
        },
        {
          path: '/by-car-parameters',
          load: () =>
            import(/* webpackChunkName: 'by-car-parameters' */ './profile/insurance/ByCarParameters'),
        },
        {
          path: '/registration-form',
          load: () =>
            import(/* webpackChunkName: 'registration-form' */ './profile/insurance/RegistrationForm'),
        },
        {
          path: '/orders',
          load: () => import(/* webpackChunkName: 'orders' */ './profile/insurance/Orders'),
        },
      ],
    },

    {
      path: '/favorites/:transportSlug?',
      load: () => import(/* webpackChunkName: 'favorites' */ './profile/favorites'),
    },

    {
      path: '/zhurnal',
      action: fetchJournalRubrics,
      children: [
        {
          path: '/video',
          load: () => import(/* webpackChunkName: 'journal' */ './journal/videos'),
        },
        {
          path: '/video/*',
          load: () => import(/* webpackChunkName: 'journal' */ './journal/videos'),
        },
        {
          path: '/:rubricSeoSlug?',
          load: () => import(/* webpackChunkName: 'journal' */ './journal/NewsComponent'),
        },
        {
          path: '/:id',
          load: () => import(/* webpackChunkName: 'news-page' */ './journal/news-page'),
        },
      ],
    },
    {
      path: '/leasing',
      // action: fetchJournalRubrics,
      children: [
        {
          path: '/*',
          load: () => import(/* webpackChunkName: 'news-page' */ './articles'),
        },
      ],
    },

    {
      path: '/my-searches',
      load: () => import(/* webpackChunkName: 'my-searches' */ './profile/my-searches'),
    },

    {
      path: '/:id',
      load: () => import(/* webpackChunkName: 'advert' */ './advert'),
    },
    // {
    //   path: '/:slug/:advertId',
    //   name: 'dealer-advert',
    //   load: () => import(/* webpackChunkName: 'dealer' */ './dealer-page/advert'),
    // },

    {
      path: '/invoice',
      load: () => import(/* webpackChunkName: 'invoice' */ './invoice'),
    },

    // Wildcard routes, e.g. { path: '*', ... } (must go last)
    {
      path: '*',
      load: () => import(/* webpackChunkName: 'not-found' */ './not-found'),
    },
  ],

  async action({ next, store, url, query, translate }) {
    // store.dispatch(loadingStart());

    try {
      // Execute each child route until one of them return the result
      const route = await next();

      // Provide default values for title, description etc.
      const pageTitlePart = route.page > 1 ? ` - ${translate('страница')} ${route.page}` : '';

      route.title = `${route.title || 'avtobazar.ua'}${pageTitlePart}`;
      route.description = `${route.description || ''}${pageTitlePart}`;
      route.url = url || '';
      route.query = query || {};

      // History
      const previousPath = store.getState().history.currentPath;
      const stringifiedQuery = queryString.stringify(query);
      const newPath = `${url}${stringifiedQuery && `?${stringifiedQuery}`}`;

      if (newPath !== previousPath) {
        if (previousPath) {
          store.dispatch(setPreviousPath(previousPath));
        }
        store.dispatch(setCurrentPath(newPath));
        store.dispatch(setCurrentRouteName(route.routeName || null));
      }

      return route;
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        store.dispatch(userLogout());

        return { redirect: '/login' };
      }

      throw err;
    }
  },
};

// The error page is available by permanent url for development mode
if (__DEV__) {
  routes.children.unshift({
    path: '/error',
    action: require('./error').default,
  });
}

export default routes;



// WEBPACK FOOTER //
// ./src/desktop/routes/index.js